import {
	type EnvironmentConfig,
	type LogFunction,
	type MutableRecordSource,
	type OperationLoader,
	RecordSource,
	RelayFeatureFlags,
	type RelayFieldLogger,
} from 'relay-runtime';
import LiveResolverStore, {
	type ActorIdentifier,
	type GetDataID,
	// @ts-ignore
} from 'relay-runtime/lib/store/experimental-live-resolvers/LiveResolverStore';
import { type Scheduler } from 'relay-runtime/lib/store/RelayStoreTypes';

import RelayDataID from '@atlassian/relay-data-id';
import type { ExternalDependencies } from '@atlassian/relay-fetch/multi-fetch';
import RelayMissingFieldHandlers from '@atlassian/relay-missing-field-handlers';
import { createNetwork, network as defaultNetwork } from '@atlassian/relay-network';
import RelayScheduler from '@atlassian/relay-scheduler';

type Props = {
	log?: EnvironmentConfig['log'];
	requiredFieldLogger?: RelayFieldLogger;
	network?: EnvironmentConfig['network'];
	getDataID?: EnvironmentConfig['getDataID'];
	recordSource?: RecordSource;
	networkOptions?: ExternalDependencies;
};

const createStore = (
	source: MutableRecordSource,
	options?: {
		actorIdentifier?: ActorIdentifier;
		gcReleaseBufferSize?: number;
		gcScheduler?: Scheduler;
		getDataID?: GetDataID;
		log?: LogFunction;
		operationLoader?: OperationLoader;
		queryCacheExpirationTime?: number;
		shouldProcessClientComponents?: boolean;
		treatMissingFieldsAsNull?: boolean;
	},
) => {
	RelayFeatureFlags.ENABLE_RELAY_RESOLVERS = true;
	return new LiveResolverStore(source, options);
};

export function getDefaultRelayEnvironmentConfig({
	log,
	requiredFieldLogger,
	network,
	networkOptions,
	getDataID,
	recordSource,
}: Props): EnvironmentConfig {
	return {
		configName: 'Atlassian Graphql Gateway',
		network: network || getEnvironmentNetwork(networkOptions),
		store: createStore(recordSource || new RecordSource(), {
			log: log ?? undefined,
			getDataID: getDataID ?? RelayDataID,
		}),
		log,
		relayFieldLogger: requiredFieldLogger,
		getDataID: getDataID ?? RelayDataID,
		scheduler: RelayScheduler,
		missingFieldHandlers: RelayMissingFieldHandlers,
	};
}

export function getSharedRelayEnvironmentConfig({
	log,
	requiredFieldLogger,
	getDataID,
	networkOptions,
}: Omit<Props, 'network'>): EnvironmentConfig;

export function getSharedRelayEnvironmentConfig({
	log,
	requiredFieldLogger,
	getDataID,
}: Omit<Props, 'network' | 'networkOptions'>): EnvironmentConfig;

export function getSharedRelayEnvironmentConfig({
	log,
	requiredFieldLogger,
	networkOptions,
	getDataID,
	recordSource,
}: Props): EnvironmentConfig {
	return {
		configName: 'Atlassian Shared Graphql Gateway',
		network: getEnvironmentNetwork(networkOptions),
		store: createStore(recordSource || new RecordSource(), {
			log: log ?? undefined,
			getDataID: getDataID ?? RelayDataID,
		}),
		log,
		relayFieldLogger: requiredFieldLogger,
		getDataID: getDataID ?? RelayDataID,
		scheduler: RelayScheduler,
		missingFieldHandlers: RelayMissingFieldHandlers,
	};
}

const getEnvironmentNetwork = (environmentOptions: Props['networkOptions']) => {
	if (environmentOptions) {
		return createNetwork(environmentOptions);
	}

	return defaultNetwork;
};
